import type React from "react";
import { twJoin } from "tailwind-merge";

export type SwitchProps = {
  id?: string;
  icon?: React.ReactNode;
  isChecked: boolean;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  "data-testid"?: string;
  ref?: React.Ref<HTMLInputElement>;
} & (
  | {
      size?: "sm" | "md";
      innerLabel?: never;
    }
  | {
      size: "lg";
      innerLabel?: string;
    }
) &
  (
    | {
        innerLabel: string;
        ariaLabel?: string;
        labels?: {
          on?: string;
          off?: string;
        };
      }
    | {
        labels: {
          on?: string;
          off?: string;
        };
        ariaLabel?: string;
      }
    | {
        ariaLabel: string;
        labels?: {
          on?: string;
          off?: string;
        };
      }
  );

export function Switch({
  id,
  size = "md",
  innerLabel,
  icon,
  isChecked,
  disabled,
  onChange,
  "data-testid": dataTestId,
  ariaLabel,
  labels,
  ref,
}: SwitchProps): React.ReactNode {
  return (
    <div className="inline-flex items-center gap-sm">
      {labels?.off && <span className="text-caption">{labels.off}</span>}
      <label
        data-testid={dataTestId}
        className={twJoin(
          "group relative flex items-center gap-x-2",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <input
          id={id}
          ref={ref}
          type="checkbox"
          role="switch"
          className="peer sr-only"
          disabled={disabled}
          onChange={onChange}
          checked={isChecked}
          aria-checked={isChecked}
          aria-label={ariaLabel}
          aria-describedby={isChecked ? labels?.on : labels?.off}
        />
        {/* Thumb */}
        <div
          className={twJoin(
            "absolute right-full flex items-center justify-center rounded-full bg-background-default text-text-secondary transition-all peer-checked:right-0 peer-checked:bg-background-default",
            size === "sm" && "switch-thumb-sm",
            size === "md" && "switch-thumb-md",
            size === "lg" && "switch-thumb-lg",
          )}
        >
          {icon}
        </div>
        {/* Track */}
        <div
          className={twJoin(
            "rounded-full bg-selection-track-off transition-colors group-hover:bg-selection-track-off-hover peer-checked:bg-selection-selected peer-checked:group-hover:bg-selection-selected-hover peer-disabled:bg-selection-disabled! peer-checked:peer-disabled:bg-selection-selected-disabled!",
            "ring-selection-border-hover ring-offset-2 peer-focus-visible:ring-1",
            size === "sm" && "switch-track-sm",
            size === "md" && "switch-track-md",
            size === "lg" && "switch-track-lg",
          )}
        />
        {innerLabel && (
          <span className="absolute top-1/2 switch-label-inset left-auto -translate-y-1/2 text-center text-caption-emphasis text-text-on-default-tertiary transition-all peer-checked:text-text-default-inverse">
            {innerLabel}
          </span>
        )}
      </label>
      {labels?.on && <span className={twJoin("text-caption", isChecked && "text-caption-emphasis")}>{labels.on}</span>}
    </div>
  );
}
